/**
 * RIP routing map + resolvers (Phase 64 / BLU-590 Track A).
 *
 * The bootleg-bridge sorts each POD print file into
 * `BOOTLEG_INPUT_DIR/<platen>/<ripProfile>/` so the RIP software applies the
 * correct ICC profile + white-underbase and the correct platen size. Today
 * that routing is hardcoded in the bridge (`getColour`/`getPlatten` in
 * `apps/bootleg-bridge/src/rip/folder-mapping.ts`) and, for the platen only,
 * ported into the backend (`platenForProductType` in
 * `apps/backend/src/lib/resize-for-platen.ts`).
 *
 * This module makes that routing a per-org JSONB doc, keyed on the parsed SKU
 * grammar (`parseSKU(sku).sku.{productType,color,size}`), with two independent
 * sub-maps per product type: colour-code → RIP profile folder, and size-code →
 * platen. The resolvers here are PURE — no I/O — and are the output-safety gate
 * that stops an operator-set (or corrupted org-supplied JSONB) value from
 * reaching an on-disk folder name.
 *
 * SECURITY: the resolved values become on-disk folder names on the RIP host
 * (`.../<platen>/<ripProfile>/...`), so traversal / separator injection must
 * die here on EVERY call:
 *   - colour → profile folders are free-text but re-validated against
 *     `^[A-Za-z0-9 _-]{1,32}$` (allows the space in `Bootleg B`, blocks
 *     `..`/`/`/`\`);
 *   - size → platen values are re-validated against the closed `PLATEN_CODES`
 *     set, which is the full DTG platen list (each has real pixel dimensions
 *     in `PLATEN_DIMS`).
 * `null` means "unmapped gap" — a CONFIGURED org fails closed (skip + surface a
 * visible, one-click-rectifiable error); an UNCONFIGURED org (no saved map)
 * keeps the bridge's built-in hardcode instead. The resolvers never throw.
 *
 * POD-only (PAP is pre-printed, never ripped).
 */
import { HOODIE_TYPES, mapColorCode, mapHoodieColorCode, } from '../sku-parser/sku-parser.js';
// ---------------------------------------------------------------------------
// Platen registry — platen codes + pixel dims. NOTE: this is NOT yet the single
// source of truth; two hand-maintained DUPLICATES exist and must be kept in
// LOCKSTEP: `apps/bootleg-bridge/src/rip/resize.ts` (`DIMS`) — a legit copy, the
// bridge is not a workspace member (native `sharp`) — and
// `apps/backend/src/lib/resize-for-platen.ts` (`DIMS` + `Platen` +
// `SMALL_PLATEN_PRODUCT_TYPES`), which is a workspace member and SHOULD import
// from here (follow-up: collapse the backend copy into this module). Until then,
// a platen code added here needs its pixel dimensions mirrored in both copies or
// a same-size heal prints at the wrong physical size. Platens are physical
// hardware, so this set changes rarely; adding one is a deliberate code change.
// ---------------------------------------------------------------------------
/**
 * Every DTG platen the print floor has, each dims-backed in `PLATEN_DIMS`.
 *
 * This file is the ONE home of both DTG lists: this platen list and
 * `PLATEN_VARIANTS` below. Adding a value is a one-line change here plus a
 * matching update to the Shopify metafield definition that carries it; the
 * database checks the FORMAT of a stored platen, never the list itself, so no
 * migration is needed. A new platen also needs its pixel dims in
 * `PLATEN_DIMS` and in both resize tables (backend `resize-for-platen.ts`,
 * bridge `rip/resize.ts`; `npm run check:resize-lockstep` compares them).
 *
 * New code that needs the full DTG set (per-size platen variant files, platen
 * size rules) imports THIS list. RIP routing's `PLATEN_CODES` is this same
 * array under its older name.
 */
export const DTG_PLATEN_CODES = ['10x12', '14x16', '16x18'];
/**
 * The RIP ROUTING platen list: the platens a per-org RIP routing map may send
 * a size to (router zod, `sanitizeRipRoutingMap`, `resolvePlaten`, the backend
 * routing cache and the RIP routing settings picker all read it).
 *
 * It IS `DTG_PLATEN_CODES` (the same array, not a copy), so routing can never
 * name a platen without pixel dims and the two lists cannot drift. Routing maps
 * are pushed to every bridge on a floor; bridges 0.13.0 and later accept
 * 16x18. An older bridge does NOT fail closed: it drops the 16x18 leaf, and
 * that size then falls back to the platen the type's other sizes share (a
 * wrong platen, with no error). It fails closed only when nothing is left to
 * fall back to (every size of the type on 16x18, or mixed platens). So the
 * deploy that widened this list to 16x18 (R4-13, plan 86-33) was gated
 * on every active bridge reporting 0.13.0 or later in `bridge_status`. A DTG
 * platen added later reaches routing at once: adding one to `DTG_PLATEN_CODES`
 * now needs the same bridge version check and the floor's hot folders first.
 * The two names stay so every existing caller keeps its import.
 */
export const PLATEN_CODES = DTG_PLATEN_CODES;
/** Target output dimensions per DTG platen code (300 DPI × inches). */
export const PLATEN_DIMS = {
    '10x12': { w: 3000, h: 3600 },
    '14x16': { w: 4200, h: 4800 },
    '16x18': { w: 4800, h: 5400 },
};
/**
 * The platen VARIANTS a product can carry a per-size print file for (for
 * example the Small and Large artwork of one design). Lives beside
 * `DTG_PLATEN_CODES` because this file is the ONE home of both DTG lists.
 * Adding a value is a one-line change here plus a matching update to the
 * Shopify metafield definition; the database checks format only.
 */
export const PLATEN_VARIANTS = ['Small', 'Large'];
/**
 * Codes (productType / colour / size KEYS): matches the colour-alias code
 * regex + the DB CHECK on `org_colour_code_aliases`.
 */
export const RIP_CODE_REGEX = /^[A-Za-z0-9]{1,10}$/;
/**
 * RIP profile folder VALUES: free-text, but the char class blocks `..`/`/`/`\`
 * while allowing the single space in `Bootleg B`. Trim leading/trailing
 * whitespace at the write layer (zod) — the value becomes an on-disk folder.
 */
export const RIP_PROFILE_FOLDER_REGEX = /^[A-Za-z0-9 _-]{1,32}$/;
/**
 * Print-file NAME code VALUES: the `-CODE` segment of
 * `{designId}-{printId}-{CODE}.arx4`. Tighter than
 * `RIP_PROFILE_FOLDER_REGEX` because the value lands in an object key AND in
 * an on-disk FILENAME segment: `.` would forge an extension, `/` and `\` are
 * path separators, a space breaks shell/tooling round-trips, and `-` is the
 * filename's own field delimiter (a code carrying one would split the name
 * into a different shape). Letters and digits only, 1-8 chars.
 */
export const PRINT_FILE_CODE_REGEX = /^[A-Za-z0-9]{1,8}$/;
// ---------------------------------------------------------------------------
// Resolvers.
// ---------------------------------------------------------------------------
/**
 * Own-property read that tolerates malformed (org-supplied JSONB) values:
 * non-objects, arrays, null, and inherited/prototype keys all yield undefined.
 * (Mirrors `getOwn` in `design-shape-map.ts`.) Exported so other shared
 * modules reuse this one instead of declaring another copy.
 */
export function getOwn(obj, key) {
    if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
        return undefined;
    }
    if (!Object.prototype.hasOwnProperty.call(obj, key)) {
        return undefined;
    }
    return obj[key];
}
/** Product-type sub-map lookup, with dual `2` prefix strip (`2HD` → `HD`). */
export function getTypeEntry(map, productType) {
    let typeEntry = getOwn(map, productType);
    if (typeEntry === undefined && productType.startsWith('2')) {
        typeEntry = getOwn(map, productType.slice(1));
    }
    return typeEntry;
}
/**
 * Resolve `(productType, colourCode)` → RIP profile folder.
 *
 * Lookup: `map[productType].colours[colourCode]`, with the dual `2` prefix
 * stripped from the product-type code when the raw code has no entry. A
 * RUNTIME `^[A-Za-z0-9 _-]{1,32}$` gate runs on the looked-up value on EVERY
 * call (org-supplied maps flow through here and the result becomes an on-disk
 * folder name). Returns `null` for any unknown/invalid input = unmapped gap.
 * Never throws.
 *
 * NOTE: pass the CANONICAL colour code (post colour-alias). The bridge runs
 * `canonicalColourCode` before its `getColour`, so callers must canonicalize
 * first for the key to line up.
 */
export function resolveRipProfile(map, productType, colourCode) {
    if (typeof productType !== 'string' || typeof colourCode !== 'string') {
        return null;
    }
    const colours = getOwn(getTypeEntry(map, productType), 'colours');
    const folder = getOwn(colours, colourCode);
    if (typeof folder !== 'string' || !RIP_PROFILE_FOLDER_REGEX.test(folder)) {
        return null;
    }
    return folder;
}
/**
 * Resolve `(productType, sizeCode)` → platen code.
 *
 * Lookup: `map[productType].sizes[sizeCode]`, dual `2` prefix stripped as
 * above. A RUNTIME gate restricts the value to the closed `PLATEN_CODES` set
 * (the DTG platen list, each with real pixel dims), so a mapped size can never
 * route to a folder without known dimensions. Returns `null` for any unknown/invalid input =
 * unmapped gap. Never throws.
 */
export function resolvePlaten(map, productType, sizeCode) {
    if (typeof productType !== 'string' || typeof sizeCode !== 'string') {
        return null;
    }
    const sizes = getOwn(getTypeEntry(map, productType), 'sizes');
    const platen = getOwn(sizes, sizeCode);
    if (typeof platen !== 'string' || !PLATEN_CODES.includes(platen)) {
        return null;
    }
    return platen;
}
/**
 * Resolve `(productType, colourCode)` → per-org print-file NAME code.
 *
 * Lookup: `map[productType].fileCodes[colourCode]`, with the dual `2` prefix
 * stripped from the product-type code when the raw code has no entry (same as
 * `resolveRipProfile`). A RUNTIME `^[A-Za-z0-9]{1,8}$` gate runs on the
 * looked-up value on EVERY call — the result becomes part of an on-disk
 * filename and a storage object key. Returns `null` for any unknown/invalid
 * input; `null` means "no per-org override", NOT a default. Never throws.
 *
 * Callers want `printFileCodeFor`, which layers the built-in fallback on top.
 *
 * `colourCode` is the RAW `sku.color` the caller parsed — NOT alias-
 * canonicalised (unlike the CUST/PW colour-alias step the bridge applies
 * before `resolveRipProfile`). Every current caller passes it that way, and
 * `printFileCodeFor`'s output is the ONLY sanctioned source for
 * `toPrintFileWithCode`'s `colorCode` argument — never compose a print-file
 * name from a hand-picked or independently-resolved code.
 */
export function resolvePrintFileCode(map, productType, colourCode) {
    if (typeof productType !== 'string' || typeof colourCode !== 'string') {
        return null;
    }
    const fileCodes = getOwn(getTypeEntry(map, productType), 'fileCodes');
    const code = getOwn(fileCodes, colourCode);
    if (typeof code !== 'string' || !PRINT_FILE_CODE_REGEX.test(code)) {
        return null;
    }
    return code;
}
/**
 * Sanitize an untrusted/raw routing map into a safe SUBSET: keep only the keys
 * and leaves that pass the output-safety gates (productType/colour/size keys →
 * `^[A-Za-z0-9]{1,10}$`; colour-folder values → trimmed
 * `^[A-Za-z0-9 _-]{1,32}$`; size values → the closed `PLATEN_CODES` set;
 * print-file name codes → trimmed `^[A-Za-z0-9]{1,8}$`), and drop everything
 * else. A per-type entry with no surviving colours/sizes/fileCodes is
 * dropped. Returns a FRESH object (safe to mutate).
 *
 * Unlike the loader's `validateRoutingMap` (whole-doc discard on any poison),
 * this preserves the good routing so a single bad leaf can't blank the map. Use
 * at any boundary that serves stored VALUES toward an on-disk folder (the bridge
 * endpoint) or re-persists a previously-stored doc (prefill) where the write-
 * path zod did not run — the tRPC write path zod + the DB `jsonb_typeof` CHECK
 * are the only guards on the store, and a direct DB/PostgREST write by an org
 * admin bypasses zod. Mirrors the colour-alias handler's skip-bad-rows posture.
 *
 * The bridge's OWN copy (`folder-mapping.ts`) has NO `fileCodes` concept at
 * all and keeps a TWO-way `hasColours || hasSizes` gate ON PURPOSE — the
 * bridge only ever routes RIP folders/platens, so a `fileCodes`-only entry
 * (this module's THREE-way gate) must never flip it into "configured" and
 * fail-closed on colours/sizes it was never given. Any future lockstep sync
 * of this function's shape to the bridge must NOT carry the third arm.
 */
export function sanitizeRipRoutingMap(raw) {
    const out = {};
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw))
        return out;
    for (const [productType, entry] of Object.entries(raw)) {
        if (!RIP_CODE_REGEX.test(productType))
            continue;
        if (typeof entry !== 'object' || entry === null || Array.isArray(entry))
            continue;
        const cleanColours = {};
        const colours = entry.colours;
        if (typeof colours === 'object' && colours !== null && !Array.isArray(colours)) {
            for (const [code, folder] of Object.entries(colours)) {
                if (!RIP_CODE_REGEX.test(code) || typeof folder !== 'string')
                    continue;
                const trimmed = folder.trim();
                if (RIP_PROFILE_FOLDER_REGEX.test(trimmed))
                    cleanColours[code] = trimmed;
            }
        }
        const cleanSizes = {};
        const sizes = entry.sizes;
        if (typeof sizes === 'object' && sizes !== null && !Array.isArray(sizes)) {
            for (const [code, platen] of Object.entries(sizes)) {
                if (RIP_CODE_REGEX.test(code) &&
                    typeof platen === 'string' &&
                    PLATEN_CODES.includes(platen)) {
                    cleanSizes[code] = platen;
                }
            }
        }
        const cleanFileCodes = {};
        const fileCodes = entry.fileCodes;
        if (typeof fileCodes === 'object' && fileCodes !== null && !Array.isArray(fileCodes)) {
            for (const [code, value] of Object.entries(fileCodes)) {
                if (!RIP_CODE_REGEX.test(code) || typeof value !== 'string')
                    continue;
                const trimmed = value.trim();
                if (PRINT_FILE_CODE_REGEX.test(trimmed))
                    cleanFileCodes[code] = trimmed;
            }
        }
        const hasColours = Object.keys(cleanColours).length > 0;
        const hasSizes = Object.keys(cleanSizes).length > 0;
        const hasFileCodes = Object.keys(cleanFileCodes).length > 0;
        // THREE-way: a fileCodes-only entry is legitimate (an org may override the
        // print-file name grammar without touching RIP folders). A two-way gate
        // here silently deletes every such entry on read AND on prefill re-persist.
        if (!hasColours && !hasSizes && !hasFileCodes)
            continue;
        const clean = {};
        if (hasColours)
            clean.colours = cleanColours;
        if (hasSizes)
            clean.sizes = cleanSizes;
        if (hasFileCodes)
            clean.fileCodes = cleanFileCodes;
        out[productType] = clean;
    }
    return out;
}
// ---------------------------------------------------------------------------
// Default resolvers — the current hardcode, used ONLY to seed prefill so an
// org starts at today's parity. Unconfigured orgs never call these (the bridge
// keeps its own hardcode); they are not a per-entry runtime fallback for a
// configured org.
// ---------------------------------------------------------------------------
/** Product-type codes that print on the small (10×12) platen. */
const SMALL_PLATEN_PRODUCT_TYPES = ['LLBYT', 'KTEE', 'BYT', 'TB'];
/**
 * The canonical ThreadHeads POD product-type catalogue — ported from the
 * bridge's `folder-mapping.ts` header comment (canonical customizer settings
 * JSON, 2026-05-12). The coverage scan's vocabulary gate seeds from this list
 * so real catalogue types that are missing from the global `sku_rules` garment
 * lists (`LLBYT`/`BXCP`/`COS`) are still recognized and reach prefill — the
 * "prefill = today's parity" guarantee depends on it.
 *
 * ⚠️ KEEP IN LOCKSTEP with the bridge's product-type catalogue
 * (`apps/bootleg-bridge/src/rip/folder-mapping.ts`).
 */
export const DEFAULT_KNOWN_PRODUCT_TYPES = [
    'TEE', // T-Shirt
    'HT', // Oversized Tee
    'HD', // Hoodie
    'SWT', // Sweatshirt
    'TSW', // (sku_rules standard garment; kept for parity with order grammar)
    'KTEE', // Kids Tee
    'WOM', // Women's Tee
    'TB', // Tote Bag
    'LLBYT', // Longline Baby Tee
    'BXCP', // Boxy Crop Tee
    'BYT', // Baby Tee
    'COS', // Cropped Oversized Tee
    'CSH', // legacy decompile — preserved for back-compat
];
/**
 * Default RIP profile for `(productType, colourCode)` — a faithful port of the
 * bridge's `getColour` (`apps/bootleg-bridge/src/rip/folder-mapping.ts`).
 *
 * ⚠️ KEEP IN LOCKSTEP with the bridge's `getColour` switch. Used only to seed
 * prefill. Total function (the default/catch-all arms always return a folder,
 * mirroring the bridge, which always routes a KNOWN colour to a folder). It
 * does NOT replicate the bridge's `DEFAULT_KNOWN_COLOUR_CODES` skip-gate — a
 * colour the bridge would skip is seeded to its `getColour` folder here, and
 * the RIP-operator ratify step + coverage view is where a should-be-skipped
 * colour gets blanked. Operates on the parseSKU product-type code (dual `2`
 * stripped) — that is the map-key basis, so `2TB` routes via the `TB` arm.
 */
export function defaultRipProfileFor(productType, colourCode) {
    const pt = productType.toUpperCase().replace(/^2/, '');
    const c = colourCode.toUpperCase();
    switch (pt) {
        case 'TB':
        case 'CSH':
        case 'BYT':
            if (c === 'BK')
                return 'Bootleg B';
            if (c === 'NT')
                return 'CO';
            return 'M';
        case 'HD':
        case 'SWT':
            if (c === 'BK')
                return 'BH';
            if (c === 'WH' || c === 'NT')
                return 'CO';
            return 'CH';
        default:
            if (c === 'BK')
                return 'Bootleg B';
            if (c === 'WH' || c === 'NT' || c === 'BR' || c === 'RR')
                return 'CO';
            if (c === 'AHST' || c === 'BKST' || c === 'CR' || c === 'NY')
                return 'N';
            return 'M';
    }
}
/**
 * Default platen for `(productType, sizeCode)` — a port of the backend's
 * `platenForProductType` (`apps/backend/src/lib/resize-for-platen.ts`) /
 * bridge `getPlatten`. Today's rule is per-product-type only, so `sizeCode` is
 * accepted (the map ENRICHES to per-size) but does not affect the default; the
 * dual `2` prefix is stripped.
 *
 * ⚠️ KEEP IN LOCKSTEP with `SMALL_PLATEN_PRODUCT_TYPES` /
 * `platenForProductType` / the bridge's `PLATTEN_TRIGGERS`.
 */
export function defaultPlatenFor(productType, _sizeCode) {
    const pt = productType.toUpperCase().replace(/^2/, '');
    return SMALL_PLATEN_PRODUCT_TYPES.includes(pt) ? '10x12' : '14x16';
}
/**
 * Default print-file NAME code for `(productType, colourCode)` — a VERBATIM
 * port of `toPrintFile`'s non-eco dispatch (`HOODIE_TYPES` → hoodie map, else
 * the standard map).
 *
 * Deliberately UNLIKE `defaultRipProfileFor`, this does NOT uppercase and does
 * NOT strip the dual `2` prefix: it must reproduce today's byte output for
 * whatever product-type string its caller passes, and the four backend call
 * sites disagree about stripping (two strip before calling, two do not). Any
 * normalization must stay at the call site so parity is preserved per-site.
 *
 * Note `TB` (tote bag) sits in `HOODIE_TYPES` today, so a black tote resolves
 * to `BH`. That is PARITY, not a fix — the per-org `fileCodes` table is how an
 * org corrects it.
 *
 * ⚠️ KEEP IN LOCKSTEP with `toPrintFile` in `../sku-parser/sku-parser.js`.
 * Eco mode is out of scope (it has no per-org map surface and no caller here).
 */
export function defaultPrintFileCodeFor(productType, colourCode) {
    return HOODIE_TYPES.has(productType)
        ? mapHoodieColorCode(colourCode)
        : mapColorCode(colourCode);
}
/**
 * The print-file NAME code for `(productType, colourCode)`: the org's mapped
 * value when present and valid, otherwise today's built-in rule.
 *
 * FAIL-OPEN by design, and the ONLY function backend filename derivations
 * should call. Contrast the RIP-folder path, which fails CLOSED for a
 * configured org: a wrong RIP profile scraps a physical garment, so an
 * unmapped colour must stop the line; a missing file-name code merely means
 * "keep today's naming grammar", so falling back is always safe and keeps an
 * org with a partial (or absent) table byte-identical to today.
 *
 * Pass the product type exactly as the call site passes it today (stripped or
 * raw) — see `defaultPrintFileCodeFor`.
 */
export function printFileCodeFor(map, productType, colourCode) {
    if (map) {
        const mapped = resolvePrintFileCode(map, productType, colourCode);
        if (mapped !== null)
            return mapped;
    }
    return defaultPrintFileCodeFor(productType, colourCode);
}
/**
 * DISPLAY-ONLY **FALLBACK** names for the built-in POD product-type codes.
 *
 * NOT the primary source. The primary source is the ORG'S OWN code table
 * (`org_product_sync_settings.productTypes`, surfaced by
 * `productSync.getCodeTablesForOrg`): an org that has named `TB` "Canvas
 * Tote" must see "Canvas Tote", never our word for it. This constant is the
 * third rung of the ladder every settings surface must walk:
 *
 *   1. the org's own `storeName` for the code (several names for one code
 *      join with ` / `),
 *   2. this table,
 *   3. the bare code.
 *
 * The settings UI shows `TB · Tote Bag` so an operator who thinks in garments
 * can find the row they mean; nothing here affects routing, filenames, or any
 * resolver above. Keys mirror `DEFAULT_KNOWN_PRODUCT_TYPES` (the map-key basis:
 * uppercase, dual `2` already stripped) and the values are the comments beside
 * that list. A code with NO entry renders as the bare code — callers must
 * degrade, never invent a label, because an org can add its own product types.
 *
 * ⚠️ Add a label whenever `DEFAULT_KNOWN_PRODUCT_TYPES` gains a code.
 */
export const PRODUCT_TYPE_LABELS = {
    TEE: 'T-Shirt',
    HT: 'Oversized Tee',
    HD: 'Hoodie',
    SWT: 'Sweatshirt',
    TSW: 'Sweatshirt (order grammar)',
    KTEE: 'Kids Tee',
    WOM: "Women's Tee",
    TB: 'Tote Bag',
    LLBYT: 'Longline Baby Tee',
    BXCP: 'Boxy Crop Tee',
    BYT: 'Baby Tee',
    COS: 'Cropped Oversized Tee',
    CSH: 'Legacy',
};
